/**
 * Canonical baked fallback for tenant branding/config fields.
 *
 * This is the single source of truth for ODL-neutral defaults used when
 * the backend /tenant/ endpoint is unreachable or has not yet responded.
 * Seeding the Redux slice from here ensures nav components (AppBar, AppNav,
 * LeftNav) paint the correct brand color on first render, eliminating the
 * flash caused by mismatched or absent baked defaults.
 *
 * Field names are the 16-field canonical allowlist — identical snake_case
 * keys on the wire for the DRF serializer, the Pydantic model, and this
 * baked fallback.
 *
 * Contains:
 * - TENANT_DEFAULTS — the frozen fallback object
 */

/**
 * Baked ODL-neutral defaults for all 16 allowlist fields.
 *
 * Immutable — callers that need to customize must spread a new object.
 *
 * @type {Readonly<{
 *   primary_color: string,
 *   secondary_color: string,
 *   navbar_color: string | null,
 *   organization_name: string,
 *   organization_name_long: string,
 *   contact_email: string,
 *   contact_phone: string,
 *   url: string,
 *   logo: string,
 *   color_logo: string,
 *   customer_term: string,
 *   site_term: string,
 *   site_term_plural: string,
 *   sitegroup_term: string,
 *   sitegroup_term_plural: string,
 *   relay_load_term: string,
 *   relay_load_term_plural: string,
 *   sales_group_id: number,
 *   disable_third_party_api: boolean,
 *   separate_tenant: boolean,
 * }>}
 */
const TENANT_DEFAULTS = Object.freeze({
  primary_color: '#0050a3',
  secondary_color: '#9e9e9e',
  // null so the navbar_color ?? primary_color fallthrough in nav consumers works correctly
  navbar_color: null,
  organization_name: 'Outdoorlink',
  organization_name_long: 'Outdoorlink, Inc.',
  contact_email: 'support@outdoorlinkinc.com',
  // Bare form avoids a double "+1" prefix — the welcome-email serializer prepends "+1 " literally
  contact_phone: '(256) 885-9768',
  url: 'https://portal.outdoorlinkinc.com',
  logo: '/static/pliny/png/odl_logo_2017_white_transparent.png',
  color_logo: '/static/pliny/png/odl_logo_2017.png',
  customer_term: 'Customer',
  site_term: 'terms.site.site',
  site_term_plural: 'terms.site.sites',
  sitegroup_term: 'terms.siteGroup.group',
  sitegroup_term_plural: 'terms.siteGroup.groups',
  relay_load_term: 'terms.relayLoad.face',
  relay_load_term_plural: 'terms.relayLoad.faces',
  sales_group_id: 1072,
  disable_third_party_api: false,
  separate_tenant: false,
});

export default TENANT_DEFAULTS;
